import {
  apCommonFields,
  apEventSpec,
  isApScreen,
  safeApProperties
} from './apAnalytics'
import {
  billsSharedProperties,
  instrumentationProperties,
  isBillsScopedEvent,
  posthogMetadata
} from './apBillsPrivacy'

type Properties = Record<string, unknown>

// Existing (legacy) events keep their names; only these fields survive.
const legacy = new Map<string, string[]>()
export const registerApLegacyEvents = (events: Record<string, string>) => {
  for (const [name, fields] of Object.entries(events))
    legacy.set(name, [
      ...(legacy.get(name) ?? []),
      ...fields.split(' ').filter(Boolean)
    ])
}

const automaticFields = [
  '$raw_user_agent',
  '$user_agent',
  '$host',
  '$session_id',
  '$window_id',
  '$insert_id',
  '$lib',
  '$lib_version',
  '$browser',
  '$browser_version',
  '$os',
  '$os_version',
  '$device_type',
  '$screen_height',
  '$screen_width',
  '$viewport_height',
  '$viewport_width',
  '$timezone',
  '$time',
  '$is_identified',
  '$user_id',
  'distinct_id',
  // $identify carries this; dropping it splits the anonymous visitor from the person.
  '$anon_distinct_id',
  // posthog-js sends the project key only here; without it ingestion
  // refuses the event with a 401.
  'token'
]
// Generic instrumentation that stays meaningful on AP screens. Raw error
// messages (`error_message`) are not in this list.
const instrumentationFields = [
  'action',
  'operation',
  'operation_type',
  'duration_ms',
  'status',
  'request_id',
  'route',
  'billID',
  'billIDs',
  'count',
  'successCount',
  'failedCount',
  'selectedCount',
  'errorId',
  'errorCode'
]
const droppedEvents = [
  '$autocapture',
  '$snapshot',
  '$heatmaps',
  '$rageclick',
  '$dead_click'
]
const contextEnums = {
  environment: ['production', 'staging', 'development'],
  trafficClass: [
    'partner_self_service',
    'internal',
    'test',
    'impersonated',
    'assisted',
    'unknown'
  ],
  inputMethod: ['pointer', 'keyboard', 'drop'],
  entryPoint: ['widget', 'help_center']
}

const apPath = /^\/dashboard\/accounts-payable(?:\/|$)/
const knownRoute =
  /^\/dashboard\/accounts-payable(?:\/(?:bills|vendors|cards|payments|pay-one)(?:\/(?:create|issue|duplicates|:id)(?:\/(?:details|edit|receiving-method|request-data))?)?)?\/?$/

// Query strings and hashes can carry search text, return URLs and secure
// tokens; unknown paths can carry anything.
export const sanitizeApUrl = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined
  try {
    const url = new URL(value, 'https://app.alternativepayments.io')
    url.search = ''
    url.hash = ''
    url.username = ''
    url.password = ''
    const path = url.pathname.replace(/[0-9a-f]{8}-[0-9a-f-]{27,}/gi, ':id')
    url.pathname = knownRoute.test(path) ? path : '/'
    return url.toString()
  } catch {
    return undefined
  }
}

const isApUrl = (value: unknown) => {
  if (typeof value !== 'string') return false
  try {
    return apPath.test(
      new URL(value, 'https://app.alternativepayments.io').pathname
    )
  } catch {
    return false
  }
}

// Every event captured on an AP screen passes through here (after the Bills
// list sanitizer, which owns its own scope).
export const sanitizeApEvent = <
  T extends { event: string; properties: Properties }
>(
  event: T
): T | null => {
  const properties = event.properties
  if (isBillsScopedEvent(event)) return event
  // A legacy name is not an AP page: zendesk_support_chat_opened also fires outside AP.
  const scoped =
    apEventSpec(event.event) !== undefined ||
    isApScreen(properties.screenId) ||
    isApUrl(properties.$current_url)
  if (!scoped) return event
  // DOM and replay payloads can contain names, amounts and bank details.
  if (droppedEvents.includes(event.event)) return null
  const safe: Properties = {}
  for (const key of automaticFields)
    if (properties[key] !== undefined) safe[key] = properties[key]
  Object.assign(safe, posthogMetadata(event))
  for (const key of ['$current_url', '$referrer']) {
    const url = sanitizeApUrl(properties[key])
    if (url) safe[key] = url
  }
  const pathname = sanitizeApUrl(properties.$pathname)
  if (pathname) safe.$pathname = new URL(pathname).pathname
  const spec = apEventSpec(event.event)
  if (spec)
    Object.assign(
      safe,
      safeApProperties([...spec.fields, ...apCommonFields], properties, {
        ...spec.enums,
        ...contextEnums
      })
    )
  else {
    Object.assign(
      safe,
      safeApProperties(
        [
          ...apCommonFields,
          ...instrumentationFields,
          ...(legacy.get(event.event) ?? [])
        ],
        properties,
        contextEnums
      ),
      billsSharedProperties(properties)
    )
    for (const key of ['action', 'operation', 'operation_type', 'status'])
      if (
        typeof properties[key] === 'string' &&
        /^[A-Za-z][\w./[\]-]{0,119}$/.test(properties[key] as string)
      )
        safe[key] = properties[key]
    Object.assign(safe, instrumentationProperties(properties))
  }
  if (isApScreen(properties.screenId)) safe.screenId = properties.screenId
  if (
    typeof properties.timestamp === 'string' &&
    /^\d{4}-\d\d-\d\dT[\d:.]+Z$/.test(properties.timestamp)
  )
    safe.timestamp = properties.timestamp
  if (event.event === '$exception') {
    // Keep grouping (type, fingerprint, stack) and drop messages, which can
    // contain provider errors, names or amounts.
    const list = Array.isArray(properties.$exception_list)
      ? (properties.$exception_list as Properties[])
      : []
    safe.$exception_list = list.map(exception => ({
      type: exception.type,
      value: typeof exception.type === 'string' ? exception.type : 'Error',
      mechanism: exception.mechanism,
      stacktrace: exception.stacktrace
    }))
    for (const key of ['$exception_fingerprint', '$exception_level'])
      if (typeof properties[key] === 'string') safe[key] = properties[key]
  }
  event.properties = safe
  return event
}
